import React, { createContext, useContext, useEffect, useState } from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";

import { LoadingPage } from "@/components/pages";
import {
  Lesson,
  LessonInteractionProfile,
  LessonPlan,
  LessonResourceState,
  Student,
} from "@/types";
import { db } from "@/utils/firebase";

// Create the context
const LessonContext = createContext<{
  lesson: Lesson | null;
  lessonPlan: LessonPlan | null;
  lessonResourceState: LessonResourceState | null;
  student: Student | null;
  lessonInteractionProfile: LessonInteractionProfile | null;
  setLessonResourceState: (state: LessonResourceState) => void;
}>({
  lesson: {} as Lesson,
  lessonPlan: null,
  lessonResourceState: {
    notifiedItems: [],
    readItems: [],
  },
  student: null,
  lessonInteractionProfile: null,
  setLessonResourceState: () => {},
});

// Create a custom hook for easy access
const useLesson = () => useContext(LessonContext);

// Provider component
export const LessonContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [lessonPlan, setLessonPlan] = useState<LessonPlan | null>(null);
  const [lessonResourceState, setLessonResourceState] =
    useState<LessonResourceState | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [lessonInteractionProfile, setLessonInteractionProfile] =
    useState<LessonInteractionProfile | null>(null);

  useEffect(() => {
    if (!lessonId) return;
    const lessonResourceState = JSON.parse(
      localStorage.getItem(lessonId) || "{}"
    );
    setLessonResourceState(lessonResourceState);
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId || !lessonResourceState) return;

    localStorage.setItem(lessonId, JSON.stringify(lessonResourceState));
  }, [lessonResourceState, lessonId]);

  useEffect(() => {
    if (!lessonId) {
      window.location.href = "https://www.tutred.com";
    }
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId) return;

    const lessonRef = doc(db, "lessons", lessonId);
    const unsubscribe = onSnapshot(
      lessonRef,
      (lessonSnap) => {
        if (!lessonSnap.exists()) {
          navigate("/");
          return;
        }
        const lessonData = {
          id: lessonSnap.id,
          ...lessonSnap.data(),
        } as Lesson;
        setLesson(lessonData);

        // Fetch student data when lesson is loaded
        if (lessonData.studentId && !student) {
          const fetchStudent = async () => {
            try {
              const studentDoc = await getDoc(
                doc(db, "students", lessonData.studentId)
              );
              if (studentDoc.exists()) {
                setStudent(studentDoc.data() as Student);
              }
            } catch (err) {
              console.error("Error fetching student profile:", err);
            }
          };
          fetchStudent();
        }

        // Set up real-time listener for lesson interaction profile
        if (lessonData.id) {
          const lessonInteractionProfileRef = doc(
            db,
            "lessonInteractionProfiles",
            lessonData.id
          );
          const unsubscribeProfile = onSnapshot(
            lessonInteractionProfileRef,
            (interactionProfileSnap) => {
              if (interactionProfileSnap.exists()) {
                const lessonInteractionProfileData = {
                  id: lessonData.id,
                  ...interactionProfileSnap.data(),
                } as LessonInteractionProfile;
                setLessonInteractionProfile(lessonInteractionProfileData);
              }
            },
            (error) => {
              console.error(
                "Error fetching lesson interaction profile:",
                error
              );
            }
          );

          // Clean up the listener when component unmounts or lessonId changes
          return () => {
            unsubscribeProfile();
          };
        }
      },
      (error) => {
        console.error("Error fetching lesson:", error);
        navigate("/");
      }
    );
    return () => unsubscribe();
  }, [lessonId, student]);

  useEffect(() => {
    if (!lessonId) return;
    const lessonPlanRef = query(
      collection(db, "lessonPlans"),
      where("lessonId", "==", lessonId)
    );
    const unsubscribe = onSnapshot(
      lessonPlanRef,
      (lessonPlanSnap) => {
        if (lessonPlanSnap.empty) {
          return;
        }
        const lessonPlanDoc = lessonPlanSnap.docs[0];
        const lessonPlan = {
          id: lessonPlanDoc.id,
          ...lessonPlanDoc.data(),
        } as LessonPlan;
        setLessonPlan(lessonPlan);
      },
      (error) => {
        console.error("Error fetching lesson plan:", error);
      }
    );
    return () => unsubscribe();
  }, [lessonId]);

  if (!lesson) {
    return <LoadingPage />;
  }

  return (
    <LessonContext.Provider
      value={{
        lesson,
        lessonPlan,
        lessonInteractionProfile,
        student,
        lessonResourceState,
        setLessonResourceState,
      }}
    >
      {children}
    </LessonContext.Provider>
  );
};

export default useLesson;
